'use server';

import { cookies } from "next/headers";
import { redirect } from 'next/navigation'
import { loginSchema } from "./schema";
import { authenticateUser } from "./service";
import { createSessionToken } from '@/lib/session'
import { logAdminAuditEvent } from '@/services/admin-audit/service'

export interface LoginState {
  errors?: {
    email?: string[];
    password?: string[];
    _form?: string[];
  };
  success?: boolean;
}

export async function login(prevState: LoginState, formData: FormData): Promise<LoginState> {
  const validatedFields = loginSchema.safeParse({
    email: formData.get("email"),
    password: formData.get("password"),
  });

  if (!validatedFields.success) {
    return { errors: validatedFields.error.flatten().fieldErrors };
  }

  try {
    const { email, password } = validatedFields.data;
    const authResult = await authenticateUser({ email, password });

    if (!authResult.success) {
      return { errors: { _form: [authResult.message] } };
    }

    const now = Math.floor(Date.now() / 1000)
    const token = await createSessionToken({
      uid: authResult.user.id,
      email: authResult.user.email,
      role: authResult.user.global_role,
      iat: now,
      exp: now + 60 * 60 * 24 * 7,
      v: 1
    })

    const cookieStore = await cookies();
    cookieStore.set("admin_session", token, {
      httpOnly: true,
      secure: process.env.NODE_ENV === "production",
      sameSite: "lax",
      maxAge: 60 * 60 * 24 * 7,
      path: "/",
    });

    // Clear legacy cookie. Session identity now comes only from signed token.
    cookieStore.set("admin_user", '', {
      httpOnly: true,
      secure: process.env.NODE_ENV === "production",
      sameSite: "lax",
      maxAge: 0,
      path: "/",
    });

    await logAdminAuditEvent({
      action: 'CREATE',
      entity: 'ADMIN_SESSION',
      category: 'AUTH',
      entityId: String(authResult.user.id),
      oldData: null,
      newData: {
        user_id: authResult.user.id,
        email: authResult.user.email,
        role: authResult.user.global_role
      }
    })
  } catch (error) {
    console.error("Login action error:", error);
    return { errors: { _form: ["An unexpected error occurred. Please try again."] } };
  }

  redirect('/')
}
